import { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, Minus, AlertTriangle } from 'lucide-react';
import ModuleContainer from '../layout/ModuleContainer.jsx';

const SOURCE_LABELS = {
  REGIME: 'Regime',
  FUTURES_ICT: 'Futures ICT',
  CONGRESSIONAL: 'Congress',
  ON_CHAIN: 'On-Chain',
  SMART_MONEY: 'Smart Money',
  CAPITULATION: 'Capitulation',
  FEAR_GREED: 'Fear/Greed',
  NEWS_SENTIMENT: 'News',
  MOMENTUM: 'Momentum',
};

const CONVICTION_COLORS = {
  HIGH: 'text-korinth-accent',
  MEDIUM: 'text-korinth-warning',
  LOW: 'text-korinth-muted',
};

function DirectionCell({ direction }) {
  if (direction === 'bullish') return <TrendingUp className="w-3.5 h-3.5 text-korinth-accent mx-auto" />;
  if (direction === 'bearish') return <TrendingDown className="w-3.5 h-3.5 text-korinth-danger mx-auto" />;
  return <Minus className="w-3 h-3 text-korinth-border mx-auto" />;
}

export default function ConvergenceScorer() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchMatrix() {
      try {
        const res = await fetch('/api/signals/convergence');
        if (res.ok) setData(await res.json());
      } catch { /* silent */ }
      finally { setLoading(false); }
    }
    fetchMatrix();
    const interval = setInterval(fetchMatrix, 60000);
    return () => clearInterval(interval);
  }, []);

  if (!data && loading) {
    return (
      <ModuleContainer title="Convergence Matrix" subtitle="Signal sources x assets — where do they agree?">
        <p className="text-xs text-korinth-muted font-mono text-center py-8">Loading convergence data...</p>
      </ModuleContainer>
    );
  }

  const { assets = [], sources = [], matrix = {}, convergenceScores = {} } = data || {};
  const displayedSources = sources.filter(s => SOURCE_LABELS[s]);

  return (
    <ModuleContainer title="Convergence Matrix" subtitle="Signal sources x assets — where do they agree?">
      <div className="overflow-x-auto">
        <table className="w-full text-[10px] font-mono">
          <thead>
            <tr className="border-b border-korinth-border">
              <th className="text-left text-korinth-muted py-2 px-1 w-24">Source</th>
              {assets.map(a => (
                <th key={a} className="text-center text-korinth-muted py-2 px-1">{a}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayedSources.map(source => (
              <tr key={source} className="border-b border-korinth-border/30">
                <td className="text-korinth-text py-1.5 px-1">{SOURCE_LABELS[source]}</td>
                {assets.map(asset => (
                  <td key={asset} className="text-center py-1.5 px-1">
                    <DirectionCell direction={matrix[asset]?.[source]} />
                  </td>
                ))}
              </tr>
            ))}
            {/* Summary row */}
            <tr className="border-t-2 border-korinth-border bg-korinth-bg/50">
              <td className="text-korinth-accent font-bold py-2 px-1">SCORE</td>
              {assets.map(asset => {
                const cs = convergenceScores[asset];
                if (!cs) return <td key={asset} className="text-center py-2 px-1"><Minus className="w-3 h-3 text-korinth-border mx-auto" /></td>;
                return (
                  <td key={asset} className="text-center py-2 px-1">
                    <div className={`text-xs font-bold ${CONVICTION_COLORS[cs.conviction] || ''}`}>
                      {cs.score}
                    </div>
                    <div className="text-[8px] text-korinth-muted">
                      {cs.sourceCount}src
                    </div>
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>

      {/* Top opportunities summary */}
      {Object.entries(convergenceScores)
        .filter(([, cs]) => cs.conviction === 'HIGH')
        .sort((a, b) => b[1].score - a[1].score)
        .length > 0 && (
        <div className="mt-4 p-3 bg-korinth-accent/5 rounded border border-korinth-accent/20">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-3 h-3 text-korinth-accent" />
            <span className="text-[10px] font-mono font-bold text-korinth-accent uppercase tracking-wider">HIGH Conviction Signals</span>
          </div>
          <div className="space-y-1">
            {Object.entries(convergenceScores)
              .filter(([, cs]) => cs.conviction === 'HIGH')
              .sort((a, b) => b[1].score - a[1].score)
              .map(([asset, cs]) => (
                <div key={asset} className="flex items-center gap-2 text-xs font-mono">
                  {cs.direction === 'bullish' ? <TrendingUp className="w-3 h-3 text-korinth-accent" /> : <TrendingDown className="w-3 h-3 text-korinth-danger" />}
                  <span className="text-white font-bold">{asset}</span>
                  <span className="text-korinth-muted">{cs.direction}</span>
                  <span className="text-korinth-accent ml-auto">{cs.score}/10 ({cs.sourceCount} sources)</span>
                </div>
              ))}
          </div>
        </div>
      )}
    </ModuleContainer>
  );
}
